import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { UserApiServiceCollection } from 'app/features/user/services/api-service/api.service.component';
import { DataService } from '../../services/data/data.service';
import { AuthService } from '@shared/services/auth.service';
import { SESSION_STORAGE_KEYS } from 'app/core/session_storage/constants/session-storage-keys.constant';
import { IsAdminService } from '@shared/services/is-admin.service';
import { IsInstanceAdminService } from '@shared/services/is-instance-admin.service';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
  imports: [ReactiveFormsModule],
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  avatar = false;
  pic: any;
  username = '';
  email = '';
  profileLoading = true;
  profileError = false;
  languageError = false;
  readonly languageControl = new FormControl<'de' | 'en'>('de', { nonNullable: true });
  isAdmin = false;
  isInstanceAdmin = false;
  private readonly destroy$ = new Subject<void>();
  private profileRequest?: Subscription;
  private pictureRequest?: Subscription;
  private profilePictureObjectUrl: string | null = null;
  private destroyed = false;

  constructor(
    private auth: AuthService,
    private apiService: UserApiServiceCollection,
    private sanitizer: DomSanitizer,
    public dataService: DataService,
    private isAdminService: IsAdminService,
    private isInstanceAdminService: IsInstanceAdminService,
  ) {}

  ngOnInit(): void {
    this.isAdminService.checkIfAdminPath();
    this.getAadProfilePicture();
    this.loadUserData();
    this.syncLanguageSelection();
    this.languageControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => this.setLanguage(language));
    this.dataService.languageChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => this.syncLanguageSelection());
    this.isAdminService.isAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
    });
    this.isInstanceAdminService.isInstanceAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isInstanceAdmin) => {
      this.isInstanceAdmin = isInstanceAdmin;
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.profileRequest?.unsubscribe();
    this.pictureRequest?.unsubscribe();
    this.clearProfilePicture();
  }

  loadUserData(): void {
    this.profileRequest?.unsubscribe();
    this.profileLoading = true;
    this.profileError = false;
    this.profileRequest = this.apiService.getCurrentUserData().subscribe({
      next: (data) => {
        this.username = data.name;
        this.email = data.email;
        this.profileLoading = false;
      },
      error: () => {
        this.profileLoading = false;
        this.profileError = true;
      },
    });
  }

  getAadProfilePicture(): void {
    this.pictureRequest?.unsubscribe();
    this.pictureRequest = this.auth.getAadProfilePicture().subscribe({
      next: (image: Blob | null) => {
        this.clearProfilePicture();
        if (image) {
          this.profilePictureObjectUrl = URL.createObjectURL(image);
          this.pic = this.sanitizer.bypassSecurityTrustUrl(this.profilePictureObjectUrl);
          this.avatar = true;
        }
      },
      error: () => this.clearProfilePicture(),
    });
  }

  clearProfilePicture(): void {
    if (this.profilePictureObjectUrl) {
      URL.revokeObjectURL(this.profilePictureObjectUrl);
      this.profilePictureObjectUrl = null;
    }
    this.avatar = false;
    this.pic = null;
  }

  setLanguage(lang: 'de' | 'en'): void {
    this.languageError = false;
    if (lang === this.dataService.getLanguage()) return;
    try {
      localStorage.setItem(SESSION_STORAGE_KEYS.CLIENT_LANGUAGE, lang);
      this.dataService.setLanguage(lang);
    } catch {
      this.languageError = true;
      // The radio accessor clears its peers after emitting the new value.
      queueMicrotask(() => {
        if (!this.destroyed) this.syncLanguageSelection();
      });
    } finally {
      this.syncLanguageSelection();
    }
  }

  private syncLanguageSelection(): void {
    this.languageControl.setValue(this.dataService.getLanguage() === 'en' ? 'en' : 'de', { emitEvent: false });
  }

  signOut(): void {
    localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
    this.auth.logout();
  }
}
